import React, { useCallback, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useNavigate } from 'react-router-dom';

import { Theme } from '@app/theme';
import {
  declinedStatus,
  paidStatus,
  Participation,
  ParticipationStatus,
  QuotaRef,
  QuotaTag,
  Session,
  StudyType,
  useApplicationsByQuotas,
  useCountParticipationByStatus,
  useGetApplicationsByCriterionAndChoices,
  useNotificationListener,
} from '@app/api';

import { MonitoringColumn } from './components/MonitoringColumn';
import { CardDragLayer } from './components/CardDragLayer';
import { ReactDndProvider } from './components/ReactDndProvider';
import { RefusedDropZone } from './components/RefusedDropZone';
import { useModalState } from '@app/core';
import { EmailContactColumnModal } from './components/EmailContactColumnModal';
import { ParticipationDetailsModalPage } from './components/ParticipationDetailsModalPage';
import { MonitoringInformationCard } from './components/MonitoringInformationCard';
import { SmsContactColumnModal } from './components/SmsContactColumnModal';
import { WithOverviewColumnLayout } from './components/WithOverviewColumnLayout';
import { getColorForParticipationStatus } from 'lib/participationUtils';
import { stringityItem } from 'lib/criteriaUtils';
import { getQuotaItemLabel } from './components/QuotaBox';
import { Trans, useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  columns: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  column: {
    marginRight: theme.marginBase * 2,
  },
}));

interface Props {
  session: Session;
  search?: string;
  canSeeQuotas?: boolean;
}

export enum Direction {
  PREVIOUS = 'PREVIOUS',
  NEXT = 'NEXT',
}

export const ParticipantMonitoring = ({
  session,
  search,
  canSeeQuotas,
}: Props) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [quotaPerApplication, setQuotaPerApplication] = useState(
    new Map<string, QuotaTag[]>(),
  );
  const [taggedCriteria, setTaggedCriteria] = useState<string[]>([]);

  const { data: participationsCountByStatuses } = useCountParticipationByStatus(
    session.id,
  );
  const { data: applicationByCriterionAndChoices } =
    useGetApplicationsByCriterionAndChoices(session.recruitmentId);
  const { data: applicationsByQuotas } = useApplicationsByQuotas(session.id);

  const getCountByStatus = useCallback(
    (statuses: ParticipationStatus[]) => {
      return statuses.reduce(
        (acc, status) => acc + (participationsCountByStatuses?.[status] || 0),
        0,
      );
    },
    [participationsCountByStatuses],
  );

  useNotificationListener(session.id);

  const openDetailModal = useCallback(
    (participation?: Participation) => {
      if (!participation) {
        return;
      }
      navigate(`part/${participation.id}`);
    },
    [navigate],
  );
  const openBookingModal = useCallback(
    (id: string) => {
      navigate(`part/${id}/book`);
    },
    [navigate],
  );
  const openCancelModal = useCallback(
    (id: string) => {
      navigate(`part/${id}/cancel`);
    },
    [navigate],
  );
  const openUpdateQuotaModal = useCallback(
    (id: string) => {
      navigate(`part/${id}/update-quota`);
    },
    [navigate],
  );

  const [
    emailContactModalStatus,
    openEmailContactModal,
    closeEmailContactModal,
  ] = useModalState<ParticipationStatus[]>([]);
  const [smsContactModalStatus, openSmsContactModal, closeSmsContactModal] =
    useModalState<ParticipationStatus[]>([]);

  const openContactParticipantsModal = useCallback(
    (status: ParticipationStatus[], type: 'email' | 'sms') => {
      if (type === 'email') {
        openEmailContactModal(status);
      } else if (type === 'sms') {
        openSmsContactModal(status);
      }
    },
    [openSmsContactModal, openEmailContactModal],
  );

  const mergedApplicationsByQuotas = useMemo(() => {
    return {
      ...(applicationsByQuotas ?? {}),
      ...(applicationByCriterionAndChoices ?? {}),
    };
  }, [applicationsByQuotas, applicationByCriterionAndChoices]);

  const onAddQuotaItemTag = useCallback(
    (criterion: QuotaRef) => {
      const reference = criterion.ref ?? criterion.fieldRef ?? '';
      setQuotaPerApplication((prev) => {
        const updated = new Map(prev);

        const byCrit = mergedApplicationsByQuotas[reference] ?? {};
        for (const key in byCrit) {
          const quota = criterion.quotas?.find(
            (q) => stringityItem(q.item) === key,
          );

          const name = quota?.label ?? getQuotaItemLabel(key, reference);
          for (const app of byCrit[key]) {
            const existing = updated.get(app) ?? [];
            if (
              !existing.some(
                (t) => t.item === key && t.color === criterion.color,
              )
            ) {
              updated.set(app, [
                ...existing,
                {
                  name,
                  color: criterion.color ?? '',
                  item: key,
                  fieldRef: reference,
                },
              ]);
            }
          }
        }

        return updated;
      });
      setTaggedCriteria((prev) =>
        prev.includes(reference) ? prev : [...prev, reference],
      );
    },
    [mergedApplicationsByQuotas],
  );

  const onRemoveQuotaItemTag = useCallback(
    (criterion: QuotaRef) => {
      const reference = criterion.ref ?? criterion.fieldRef ?? '';
      setQuotaPerApplication((prev) => {
        const byCrit = mergedApplicationsByQuotas[reference] ?? {};

        const itemsToRemove = new Set<string>();
        for (const key in byCrit) {
          itemsToRemove.add(key);
        }

        let changed = false;
        const updated = new Map(prev);

        for (const [app, tags] of Array.from(updated.entries())) {
          const filtered = tags.filter((tag) => !itemsToRemove.has(tag.item));
          if (filtered.length !== tags.length) {
            changed = true;
            if (filtered.length > 0) updated.set(app, filtered);
            else updated.delete(app);
          }
        }

        return changed ? updated : prev;
      });
      setTaggedCriteria((prev) =>
        prev.includes(reference) ? prev.filter((r) => r !== reference) : prev,
      );
    },
    [mergedApplicationsByQuotas],
  );

  const numberOfAcceptedAndBooked = useMemo(() => {
    return (
      getCountByStatus([ParticipationStatus.ACCEPTED]) + session.numberOfBooked
    );
  }, [getCountByStatus, session.numberOfBooked]);

  const numberOfParticipated = useMemo(() => {
    return getCountByStatus([
      ParticipationStatus.PARTICIPATED,
      ParticipationStatus.PAYMENT_REQUESTED,
      ParticipationStatus.PROCESSING_PAYMENT,
      ParticipationStatus.PAYED,
    ]);
  }, [getCountByStatus]);

  const tooMuchParticipantApplied = useMemo(() => {
    return (
      getCountByStatus([ParticipationStatus.APPLIED]) >=
      (session.numberOfParticipantRequired - session.numberOfBooked) * 2
    );
  }, [session, getCountByStatus]);

  return (
    <React.Fragment>
      <ReactDndProvider>
        <WithOverviewColumnLayout
          session={session}
          horizontalScroll
          contentClassName={classes.columns}
          taggedCriteria={taggedCriteria}
          onAddQuotaItemTag={onAddQuotaItemTag}
          onRemoveQuotaItemTag={onRemoveQuotaItemTag}
        >
          <MonitoringColumn
            className={classes.column}
            status={ParticipationStatus.INVITED}
            onParticipationClick={openDetailModal}
            sessionId={session.id}
            onMenuItemClick={openContactParticipantsModal}
            studyType={session.studyType}
            search={search}
            hideWhenEmpty
          />
          {canSeeQuotas && (
            <MonitoringColumn
              className={classes.column}
              status={ParticipationStatus.OUT_OF_QUOTA}
              onParticipationClick={openDetailModal}
              sessionId={session.id}
              onMenuItemClick={openContactParticipantsModal}
              studyType={session.studyType}
              search={search}
              quotasPerApplication={quotaPerApplication}
              hideWhenEmpty
            />
          )}
          <MonitoringColumn
            className={classes.column}
            status={ParticipationStatus.APPLIED}
            onParticipationClick={openDetailModal}
            color={getColorForParticipationStatus(ParticipationStatus.APPLIED)}
            lock={tooMuchParticipantApplied}
            sessionId={session.id}
            onMenuItemClick={openContactParticipantsModal}
            studyType={session.studyType}
            search={search}
            informationCard={
              <MonitoringInformationCard
                text={t('sessions.details.monitoring.informationCard.review')}
                sessionId={session.id}
                color={getColorForParticipationStatus(
                  ParticipationStatus.APPLIED,
                )}
                icon="ri-lock-line"
                iconColor="red"
                dismissValue="dismissInformationReviewCard"
                hidden={
                  !tooMuchParticipantApplied ||
                  session.dismissInformationReviewCard
                }
              />
            }
            quotasPerApplication={quotaPerApplication}
          />
          <MonitoringColumn
            className={classes.column}
            status={ParticipationStatus.ACCEPTED}
            onParticipationClick={openDetailModal}
            sessionId={session.id}
            onMenuItemClick={openContactParticipantsModal}
            studyType={session.studyType}
            search={search}
            informationCard={
              <MonitoringInformationCard
                text={t('sessions.details.monitoring.informationCard.accepted')}
                sessionId={session.id}
                color={getColorForParticipationStatus(
                  ParticipationStatus.ACCEPTED,
                )}
                dismissValue="dismissInformationAcceptedCard"
                hidden={
                  !(
                    numberOfAcceptedAndBooked >=
                      session.numberOfParticipantRequired &&
                    session.numberOfParticipantRequired <
                      numberOfAcceptedAndBooked * 1.5
                  ) ||
                  session.dismissInformationAcceptedCard ||
                  session.studyType === StudyType.UNMODERATED
                }
              />
            }
            quotasPerApplication={quotaPerApplication}
            onDragFromOFQ={openUpdateQuotaModal}
          />
          {session.studyType === StudyType.UNMODERATED && (
            <MonitoringColumn
              className={classes.column}
              status={ParticipationStatus.IN_PROGRESS}
              onParticipationClick={openDetailModal}
              sessionId={session.id}
              onMenuItemClick={openContactParticipantsModal}
              studyType={session.studyType}
              search={search}
              quotasPerApplication={quotaPerApplication}
            />
          )}
          {session.studyType === StudyType.MODERATED && (
            <MonitoringColumn
              className={classes.column}
              status={ParticipationStatus.BOOKED}
              onParticipationClick={openDetailModal}
              sessionId={session.id}
              onMenuItemClick={openContactParticipantsModal}
              onDragToBook={openBookingModal}
              studyType={session.studyType}
              search={search}
              informationCard={
                <MonitoringInformationCard
                  text={
                    <Trans
                      i18nKey="sessions.details.monitoring.informationCard.booked.text"
                      components={{ b: <b /> }}
                    />
                  }
                  sessionId={session.id}
                  color={getColorForParticipationStatus(
                    ParticipationStatus.BOOKED,
                  )}
                  dismissValue="dismissInformationBookedCard"
                  buttons={[
                    {
                      href: t(
                        'sessions.details.monitoring.informationCard.booked.buttons.participation.href',
                      ),
                      label: t(
                        'sessions.details.monitoring.informationCard.booked.buttons.participation.label',
                      ),
                    },
                    {
                      href: t(
                        'sessions.details.monitoring.informationCard.booked.buttons.cancellation.href',
                      ),
                      label: t(
                        'sessions.details.monitoring.informationCard.booked.buttons.cancellation.label',
                      ),
                    },
                  ]}
                  hidden={
                    !(session.numberOfBooked >= 1) ||
                    session.dismissInformationBookedCard
                  }
                />
              }
              quotasPerApplication={quotaPerApplication}
            />
          )}
          <MonitoringColumn
            className={classes.column}
            status={ParticipationStatus.PARTICIPATED}
            onParticipationClick={openDetailModal}
            sessionId={session.id}
            onMenuItemClick={openContactParticipantsModal}
            studyType={session.studyType}
            color={getColorForParticipationStatus(
              ParticipationStatus.PARTICIPATED,
            )}
            search={search}
            informationCard={
              <MonitoringInformationCard
                text={
                  <Trans
                    i18nKey="sessions.details.monitoring.informationCard.rating.text"
                    components={{ b: <b /> }}
                  />
                }
                sessionId={session.id}
                color={getColorForParticipationStatus(
                  ParticipationStatus.PARTICIPATED,
                )}
                dismissValue="dismissInformationRatingCard"
                buttons={[
                  {
                    href: t(
                      'sessions.details.monitoring.informationCard.rating.button.href',
                    ),
                    label: t(
                      'sessions.details.monitoring.informationCard.rating.button.label',
                    ),
                  },
                ]}
                hidden={
                  !(numberOfParticipated >= 1) ||
                  session.dismissInformationRatingCard
                }
              />
            }
            quotasPerApplication={quotaPerApplication}
          />
          <MonitoringColumn
            className={classes.column}
            status={paidStatus}
            onParticipationClick={openDetailModal}
            sessionId={session.id}
            onMenuItemClick={openContactParticipantsModal}
            studyType={session.studyType}
            search={search}
            quotasPerApplication={quotaPerApplication}
          />
          <MonitoringColumn
            className={classes.column}
            status={declinedStatus}
            onParticipationClick={openDetailModal}
            onDragToCancel={openCancelModal}
            sessionId={session.id}
            onMenuItemClick={openContactParticipantsModal}
            studyType={session.studyType}
            search={search}
            quotasPerApplication={quotaPerApplication}
          />
          <RefusedDropZone
            onDragToCancel={openCancelModal}
            studyType={session.studyType}
          />
          <CardDragLayer />
        </WithOverviewColumnLayout>
      </ReactDndProvider>
      <ParticipationDetailsModalPage sessionId={session.id} />
      <EmailContactColumnModal
        onRequestClose={closeEmailContactModal}
        isOpen={!!emailContactModalStatus.length}
        status={emailContactModalStatus}
        sessionId={session.id}
        studyType={session.studyType}
      />
      <SmsContactColumnModal
        onRequestClose={closeSmsContactModal}
        isOpen={!!smsContactModalStatus.length}
        status={smsContactModalStatus}
        sessionId={session.id}
        studyType={session.studyType}
      />
    </React.Fragment>
  );
};
